import React from "react";
import "../../../styles/skeleton.css";
import { useDesign } from "../../../context/DesignContext";
import LandingPreviewSkeleton from "./LandingPreviewSkeleton";
import type { DesignTokens as LandingDesignTokens } from "./LandingPreviewSkeleton";
import FeedPosts from "./skeletonComponents/feedPosts";
import {
  coerceEmbedWidthPercent,
  getEmbedMaxWidthCss,
  EMBED_WIDTH_DEFAULT,
} from "../../../Utils/embedWidth";
import {
  coerceCreatorPageWidthPercent,
  getCreatorPageMaxWidthCss,
} from "../../../features/creator-pages/creatorPageWidth";
import { useUserDesignTokens } from "../../../Utils/customHooks/useUserDesignTokens";

interface FrontendSkeletonProps {
  from?: string;
  username?: string;
  /** Optional override (e.g. follower preview initial load) */
  designTokens?: Partial<LandingDesignTokens>;
  isIndividual?: boolean;
  isContactPage?: boolean;
  isSearch?: boolean;
  /** Saved embed width % (60–90); reserved for callers — shell is applied by parent container */
  embedMaxWidthPercent?: number;
  /** When false, embed search/feed uses full width like link-in-bio */
  applyEmbedShellWidth?: boolean;
  isInitialLoader?: boolean;
  isEmbedInitialLoader?: boolean;
}

const FrontendSkeleton = (props: FrontendSkeletonProps) => {
  const {
    from,
    username,
    designTokens: designTokensProps,
    isIndividual,
    isContactPage,
    isSearch,
    embedMaxWidthPercent,
    applyEmbedShellWidth = true,
    isInitialLoader = false,
    isEmbedInitialLoader = false
  } = props;
  const { templateName, primaryColor, secondaryColor, tertiaryColor, fontFamily } =
  useDesign();

  const designTokensEarlyQuery = useUserDesignTokens(username);
  const fromEarly =
    designTokensEarlyQuery.data ?? {}

  // Normalize into the strict token shape expected by `LandingPreviewSkeleton`.
  const designTokens: LandingDesignTokens = {
    templateName: fromEarly?.template_name ?? designTokensProps?.templateName ?? templateName ?? "",
    primaryColor: fromEarly?.template_name ?? designTokensProps?.primaryColor ?? primaryColor ?? "",
    secondaryColor: designTokensProps?.secondaryColor ?? secondaryColor ?? "",
    tertiaryColor: designTokensProps?.tertiaryColor ?? tertiaryColor ?? "",
    fontFamily: designTokensProps?.fontFamily ?? fontFamily ?? "",
  };

  const effectiveTemplateName = designTokens?.templateName || "";
  const isDefaultTheme = effectiveTemplateName.toLowerCase() === "default";
  const isEmbedSearchSkeleton = from === "embed-search";
  const embedWidthPct = coerceEmbedWidthPercent(
    embedMaxWidthPercent ?? EMBED_WIDTH_DEFAULT
  );
  const embedMaxWidthCss = getEmbedMaxWidthCss(embedWidthPct);

  const skeletonBlockFill = isDefaultTheme
    ? { backgroundColor: "#ededed" }
    : {
        background: designTokens.primaryColor || "#ededed",
        backgroundImage: "none",
      };

  // Embed loader fills the outer shell (configured rem width on parent).
  // Never set a separate 45rem / rem cap here — that causes loader↔content thrash.
  const embedSkeletonStyle =
    isEmbedSearchSkeleton && !isInitialLoader
      ? {
          minHeight: "100vh",
          width: "100%",
          maxWidth: isEmbedInitialLoader ? "100%" :embedMaxWidthCss,
          backgroundColor: designTokens.primaryColor || "transparent",
          borderRadius: "20px",
          marginTop: "56px",
          marginLeft: "auto",
          marginRight: "auto",
          boxSizing: "border-box" as const,
        }
      : null;

  return (
    <div
      id="app-wrapper"
      className={
        isEmbedSearchSkeleton
          ? "frontend-skeleton embed-skeleton-embed-width"
          : "frontend-skeleton"
      }
      style={{
        ...(isInitialLoader
          ? {
              width: "100%",
              maxWidth: "100%",
              backgroundColor: "transparent",
              marginTop: 0,
              borderRadius: 0,
            }
          : embedSkeletonStyle
            ? embedSkeletonStyle
            : {
                minHeight: "100vh",
                width: "45rem",
                maxWidth: "100%",
                backgroundColor: designTokens.primaryColor || "transparent",
                borderRadius: "20px",
                marginTop: "56px",
              }),
        fontFamily: designTokens.fontFamily || undefined,
        color: designTokens.tertiaryColor || undefined,
        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
      }}
    >
      {/* Profile Section */}
      {from === "embed-search" ? (
        <></>
      ) : (
        <>
          <div className="profile-section">
            <div
              className="avatar-skeleton"
              style={{
                ...skeletonBlockFill,
                border: `1px solid ${
                  designTokens.tertiaryColor && !isDefaultTheme? designTokens.tertiaryColor : "white"
                }`,
              }}
            ></div>
            <div
              className="text-skeleton name-text"
              style={{
                ...skeletonBlockFill,
                borderRadius: "6px",
                border: `1px solid ${
                  designTokens.tertiaryColor && !isDefaultTheme ? designTokens.tertiaryColor : "white"
                }`,
              }}
            ></div>
          </div>

          <div className="text-center">
            <div
              className="text-skeleton bio-text"
              style={{
                ...skeletonBlockFill,
                borderRadius: "6px",
                border: `1px solid ${
                  designTokens.tertiaryColor  && !isDefaultTheme ? designTokens.tertiaryColor : "white"
                }`,
              }}
            ></div>
          </div>

          <div className="social-icons">
            {[...Array(8)].map((_, index) => (
              <div
                key={index}
                className="icon-skeleton"
                style={{
                  ...skeletonBlockFill,
                  border: `1px solid ${
                    designTokens.tertiaryColor && !isDefaultTheme ? designTokens.tertiaryColor : "white"
                  }`,
                }}
              ></div>
            ))}
          </div>
        </>
      )}

      {/* Product Cards */}
      {isSearch ? (
         <div className="feed-posts-wrapper">
         <FeedPosts designTokens={designTokens} />
       </div>
      ) : (
      <div
        className="uptodate-wrapper"
      >
        <LandingPreviewSkeleton
          isDetailView={Boolean(isIndividual)}
          designTokens={designTokens}
          arrayLength={8}
          blockArray={isContactPage ? 1 : 3}
          isSearch={isSearch}
          // from={from}
          isDashboard={false}
          />
        </div>
      )}
    </div>
  );
};

export default FrontendSkeleton;
